import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Compass } from "lucide-react";
import { getSkillInTheWild } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { applyShortNames } from "../lib/skillNames";

// Summer date gate — mirrors backend `skill_in_the_wild._is_summer`
// (July + August, UTC-adjacent enough that per-teacher clock skew is
// irrelevant for a Q3-July / Q4-Aug pill). Frontend-only cue that the
// SITW copy has shifted framing; backend `payload.term` still returns
// "Term 2" through the summer.
const _isSummer = (date = new Date()) => {
  const m = date.getMonth() + 1;
  return m === 7 || m === 8;
};

/**
 * Today's Skill in the Wild — dashboard sidebar widget.
 *
 * Sits beside the generator (replaces the older CoverageWidget) and
 * surfaces one transferable skill per day with a real-world career
 * story written by Claude. The backend caches the payload per
 * (teacher, date) so this is one Claude call per day per teacher.
 *
 * CTAs:
 *   - cta_kind = "generate"      → pre-fill Skill Focus on the
 *     generator and scroll to it
 *   - cta_kind = "see_coverage"  → navigate to /dashboard?tab=coverage
 */
export const SkillInTheWildCard = ({ refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Free-tier teachers (and signed-out visitors) get a stripped-down
  // version of the card: skill name + one-line nudge + CTA into the
  // Skills Generator with the skill pre-filled. The full Claude-written
  // career story + coverage note is the paid-tier flourish.
  //
  // BETA OVERRIDE (Feb 2026): Stripe UI is hidden, so no real user has
  // an "active" subscription yet. Treat everyone as paid during beta so
  // they can see the full widget. When Stripe goes live, drop this
  // override and the original `isPaid` check below kicks in.
  const BETA_TREAT_AS_PAID = true;
  const isPaid = BETA_TREAT_AS_PAID || Boolean(
    user?.subscription?.status &&
      ["active", "trialing"].includes(user.subscription.status)
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getSkillInTheWild()
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        if (status === 401) {
          setError("auth");
        } else {
          setError("generic");
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshKey]);

  const handleCtaClick = (e) => {
    e.preventDefault();
    if (!data) return;
    if (data.cta_kind === "see_coverage") {
      navigate("/dashboard?view=tracker");
      return;
    }
    // generate path — pre-fill skill focus + scroll to generator
    try {
      sessionStorage.setItem("atomicedu_prefill_skill_focus", data.skill_library_name);
    } catch (_err) {
      /* sessionStorage blocked — fine, user can pick manually */
    }
    const section = document.querySelector('[data-testid="generator-section"]');
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
      // Fire a focus event so the GeneratorForm picks up the prefill.
      window.dispatchEvent(new Event("focus"));
    }
  };

  return (
    <aside
      data-testid="skill-in-the-wild-card"
      className="ae-card p-6 md:p-7 shadow-md sticky top-24"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-[#f4f5f7] border border-[#4a6984]/15 grid place-items-center">
            <Compass className="h-4 w-4 text-[#4a6984]" />
          </div>
          <span className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
            Today&apos;s Skill in the Wild
          </span>
        </div>
        {data?.term && (
          <span className="ae-pill" data-testid="skill-in-the-wild-term">
            {/* Lane 2 (Feb 09 2026) — frontend Summer pill override.
                July + August swap the term label to "Summer" as a
                visual cue that the coverage note has shifted to
                forward-looking framing (see `_is_summer` in the SITW
                backend module). September onward returns to normal
                automatically. */}
            {_isSummer() ? "Summer" : data.term}
          </span>
        )}
      </div>

      {loading && (
        <div data-testid="skill-in-the-wild-loading" className="space-y-3">
          <div className="h-3 rounded bg-[#4a6984]/10 animate-pulse" />
          <div className="h-3 rounded bg-[#4a6984]/10 animate-pulse w-11/12" />
          <div className="h-3 rounded bg-[#4a6984]/10 animate-pulse w-9/12" />
          <div className="h-3 rounded bg-[#4a6984]/10 animate-pulse w-5/12 mt-5" />
        </div>
      )}

      {!loading && error === "auth" && (
        <div data-testid="skill-in-the-wild-auth-prompt">
          <p className="text-sm text-[#4a6984]/80 font-medium leading-relaxed">
            Sign in to see today&apos;s skill spotlight — a daily nudge that connects one
            transferable skill to a real-world career.
          </p>
          <div className="mt-4 flex items-center gap-1.5 text-[#1c2d3d]">
            <Sparkles className="h-3.5 w-3.5" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">
              Personalised to your coverage
            </span>
          </div>
        </div>
      )}

      {!loading && error === "generic" && (
        <p
          data-testid="skill-in-the-wild-error"
          className="text-sm text-[#4a6984]/70"
        >
          Couldn&apos;t load today&apos;s spotlight. Refresh in a moment.
        </p>
      )}

      {!loading && !error && data && (
        <div className="space-y-4" data-testid="skill-in-the-wild-content">
          <p
            data-testid="skill-in-the-wild-skill-name"
            className="font-bold uppercase text-xs tracking-[0.2em] text-[#1c2d3d]"
          >
            {data.skill_library_name}
          </p>

          {isPaid ? (
            <>
              <p
                data-testid="skill-in-the-wild-story"
                className="text-sm text-[#4a6984]/90 font-medium leading-relaxed"
              >
                {data.story}
              </p>

              <p
                data-testid="skill-in-the-wild-coverage-note"
                className="text-sm text-[#4a6984] font-semibold leading-relaxed border-t border-[#4a6984]/10 pt-4"
              >
                {/* Lane 2 (Feb 09 2026) — short-name swap for in-sentence
                    skill references. The card headline above keeps the
                    full canonical name; only body sentences get the
                    "Innovation & Creativity" style compression. */}
                {applyShortNames(data.coverage_note)}
              </p>

              <button
                type="button"
                data-testid="skill-in-the-wild-cta"
                onClick={handleCtaClick}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-[#1c2d3d] hover:text-[#4a6984] transition-colors"
              >
                {(() => {
                  // Strip any trailing arrow glyphs the LLM tucks onto
                  // the cta_text — the prompt asks Claude to end with
                  // a "→", but we render our own <ArrowRight /> icon
                  // directly after this text so a literal arrow in the
                  // string produces a doubled-arrow visual.
                  //
                  // Lane 2 (Feb 09 2026) — spec verbatim: "Build with
                  // this skill →" (no skill name — the card headline
                  // already carries the full name). The see_coverage
                  // branch keeps its earned recognition copy at ≥6
                  // coverage (founder decision (b) in the ask_human
                  // for this lane).
                  const raw =
                    data.cta_kind === "see_coverage"
                      ? (data.cta_text || "See your full skill coverage")
                      : "Build with this skill";
                  return applyShortNames(String(raw))
                    .replace(/[\s→➜➞➝➔►»>]+$/u, "")
                    .trim();
                })()}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              <p
                data-testid="skill-in-the-wild-free-nudge"
                className="text-sm text-[#4a6984]/90 font-medium leading-relaxed border-t border-[#4a6984]/10 pt-4"
              >
                Create a <span className="font-extrabold text-[#1c2d3d]">{data.skill_library_name}</span> activity today.
              </p>
              <button
                type="button"
                data-testid="skill-in-the-wild-free-cta"
                onClick={() => {
                  try {
                    sessionStorage.setItem(
                      "atomicedu_prefill_skill_focus",
                      data.skill_library_name
                    );
                  } catch (_e) {
                    /* sessionStorage blocked — fine */
                  }
                  navigate(`/generator?skill=${encodeURIComponent(data.skill_library_name)}#skills-generator`);
                }}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1c2d3d] hover:text-[#4a6984] transition-colors"
              >
                Generate {applyShortNames(data.skill_library_name)} activity
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      )}
    </aside>
  );
};

export default SkillInTheWildCard;
